'use client';

import { useEffect, useState } from 'react';
import { isCapacitor, isTauri } from '@/lib/platformUtils';

/**
 * Detects whether the UI should render in "mobile viewport" mode.
 *
 * Returns `true` when ANY of the following are true:
 *   - Running inside a Capacitor native app (Android/iOS)
 *   - Running inside a Tauri desktop app in a narrow window
 *   - Browser viewport width is below the `breakpoint` (default 1024px)
 *
 * Components use this to replace full-width headers/sidebars with a
 * floating action button + bottom sheet on small screens.
 */
export function useIsMobileViewport(breakpoint = 1024): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return isCapacitor() || window.innerWidth < breakpoint;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const check = () => {
      // Capacitor native apps are always mobile regardless of viewport width
      if (isCapacitor()) {
        setIsMobile(true);
        return;
      }
      // Tauri desktop is never mobile (even if window is narrow)
      if (isTauri()) {
        setIsMobile(window.innerWidth < breakpoint);
        return;
      }
      setIsMobile(window.innerWidth < breakpoint);
    };

    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [breakpoint]);

  return isMobile;
}
